import React from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { BidoraMark } from "@/components/BidoraLogo";

/**
 * SplashIntro
 *
 * App-open splash for Bidorah — a luxury-collectible live-auction marketplace.
 * Combines a centered Bidorah wordmark hero (elastic spring bounce) with a
 * 22-item product constellation that flies in around it on a staggered
 * timeline.  The intro is wholly original to Bidorah:
 *  - Brand teal #1D78AB background with a soft amber spotlight glow
 *  - Floating transparent-PNG product cutouts drift in from the edges on a
 *    staggered spring timeline, then breathe
 *  - Hand-drawn doodle stickers (lightning, heart, sparkle, eye, gavel)
 *  - Center hero — either the Bidorah wordmark (default) or the Bidora
 *    diamond mark (legacy) — joined by rotating sparkle particles + halo
 *  - Auto-dismisses after ~2200ms; tap anywhere also dismisses early
 *  - Skipped on subsequent navigations in the same session
 */

const STORAGE_KEY = "bidora.splash.shown.v20";
const DURATION_MS = 3000;

// ─── Bidorah hero wordmark intro variant ───
// DEFAULT: "B" (parallel) — the Bidorah wordmark bounce plays simultaneously
// with the 22-item product constellation. This is the brand hero splash
// every user sees on every app open.
//
// Override via `?intro=A | B | C | OFF` query param for previewing:
//   A   = SEQUENCED       — wordmark bounces in alone first, then constellation arrives
//   B   = PARALLEL        — wordmark bounces in *with* the constellation (DEFAULT)
//   C   = WORDMARK-ONLY   — wordmark bounce only, no product collage
//   OFF = original spinning diamond mark + constellation (legacy)
function readIntroStyle() {
  if (typeof window === "undefined") return "B";
  try {
    const p = new URLSearchParams(window.location.search).get("intro");
    if (p) {
      const v = p.toUpperCase();
      if (v === "OFF") return null;
      if (["A", "B", "C"].includes(v)) return v;
    }
  } catch { /* ignore */ }
  return "B";
}
// Outer ring at 30° intervals on a 42×42 ellipse around (50,50).
// Middle ring at 36° intervals on a 22×22 ellipse around (50,50).
// Positions below are computed from those equations to exact 0.5% precision.

// 22 real product PNGs arranged on TWO mathematically perfect concentric
// ellipses — outer 12 at exact 30° clock positions, middle 10 at exact 36°
// intervals.  No empty zones, every item lies precisely on its ring.
const TILES = [
  // ─── OUTER RING (12 items, every 30° clockwise from 12 o'clock) ───
  { src: "/splash/tourm-cluster.png", alt: "Tourmaline cluster", x: 50, y: 8,  r:  4, d: 0.04, w: 84,  h: 154 },
  { src: "/splash/ps5.png",           alt: "PlayStation 5",       x: 71, y: 14, r:  6, d: 0.07, w: 96,  h: 140 },
  { src: "/splash/tourm-single.png",  alt: "Tourmaline column",   x: 86, y: 29, r: -6, d: 0.10, w: 58,  h: 154 },
  { src: "/splash/louisv.png",        alt: "Louis Vuitton",       x: 92, y: 50, r:  4, d: 0.13, w: 130, h: 122 },
  { src: "/splash/azurite.png",       alt: "Azurite",             x: 86, y: 71, r: -8, d: 0.16, w: 118, h: 98  },
  { src: "/splash/cologne.png",       alt: "Versaroma cologne",   x: 71, y: 86, r: -8, d: 0.19, w: 80,  h: 122 },
  { src: "/splash/sneaker.png",       alt: "Air Jordan 1 x Dior", x: 50, y: 92, r:  4, d: 0.22, w: 162, h: 94  },
  { src: "/splash/malachite.png",     alt: "Malachite",           x: 29, y: 86, r:  6, d: 0.25, w: 82,  h: 138 },
  { src: "/splash/chanel.png",        alt: "Chanel handbag",      x: 14, y: 71, r:  6, d: 0.28, w: 122, h: 108 },
  { src: "/splash/gucci.png",         alt: "Gucci duffel",        x: 8,  y: 50, r: -12, d: 0.31, w: 118, h: 102 },
  { src: "/splash/jordan-card.jpg",   alt: "Michael Jordan card", x: 14, y: 29, r: -10, d: 0.34, w: 100, h: 140, isCard: true },
  { src: "/splash/crystal.png",       alt: "Larimar crystal",     x: 29, y: 14, r: -6, d: 0.37, w: 78,  h: 106 },

  // ─── MIDDLE RING (10 items, every 36° clockwise from 12 inner) ───
  { src: "/splash/quartz-teal.png",   alt: "Teal quartz",         x: 50, y: 29, r: -4, d: 0.40, w: 76,  h: 114 },
  { src: "/splash/aqua-crystal.png",  alt: "Aqua aragonite",      x: 63, y: 33, r:  6, d: 0.42, w: 90,  h: 86  },
  { src: "/splash/charizard-card.jpg", alt: "Charizard 1st-ed card",   x: 71, y: 44, r:  8, d: 0.44, w: 76, h: 100, isCard: true },
  { src: "/splash/supreme.png",       alt: "Supreme x LV hoodie", x: 71, y: 56, r:  8, d: 0.46, w: 78,  h: 96  },
  { src: "/splash/pikachu.png",       alt: "Pikachu",             x: 63, y: 67, r: -6, d: 0.48, w: 82,  h: 86  },
  { src: "/splash/fluorite.png",      alt: "Fluorite",            x: 50, y: 71, r: -6, d: 0.50, w: 118, h: 56  },
  { src: "/splash/alien-eye.png",     alt: "Alien-eye fluorite",  x: 37, y: 67, r:  4, d: 0.52, w: 100, h: 88  },
  { src: "/splash/eevee.png",         alt: "Eevee",               x: 29, y: 56, r:  6, d: 0.54, w: 72,  h: 90  },
  { src: "/splash/aragonite.png",     alt: "UV aragonite",        x: 29, y: 44, r: -12, d: 0.56, w: 96,  h: 60 },
  { src: "/splash/gengar.png",        alt: "Gengar",              x: 37, y: 33, r: -8, d: 0.58, w: 80,  h: 94  },
];

// Read animation style from URL: ?splash=A | B | C | D
// A = spring-scatter (default, items fly in from off-screen)
// B = confetti-drop  (items fall from top with gravity bounce)
// C = iris-bloom     (items scale up from centerpiece outwards)
// D = cascade-wave   (items fade in clockwise like a sweep)
function readAnimStyle() {
  if (typeof window === "undefined") return "A";
  try {
    const p = new URLSearchParams(window.location.search).get("splash");
    if (p && ["A", "B", "C", "D"].includes(p.toUpperCase())) return p.toUpperCase();
  } catch { /* ignore */ }
  return "A";
}

// Returns a uniform scale factor to shrink the entire splash on smaller
// viewports.  Sizes in TILES are tuned for 1440-1920px desktop; on phones we
// shrink everything proportionally so items don't overlap.
function readSplashScale() {
  if (typeof window === "undefined") return 1;
  const vw = window.innerWidth || 1440;
  if (vw < 400) return 0.40;
  if (vw < 500) return 0.46;
  if (vw < 700) return 0.58;
  if (vw < 900) return 0.70;
  if (vw < 1200) return 0.85;
  return 1;
}

// 22 real product PNGs arranged on TWO perfectly even concentric ellipses:
//   • Outer ring: 12 items at clock positions (30° each, x-radius 42, y-radius 42)
//   • Middle ring: 10 items at 36° intervals (x-radius 22, y-radius 21)
// No empty zones — the entire canvas reads as a curated luxury collage.

const STICKERS = [
  { glyph: "lightning", x: 30, y: 12, r: -8,  d: 0.34, scale: 0.9, color: "#FCD34D" },
  { glyph: "heart",     x: 60, y: 86, r:  6,  d: 0.36, scale: 1.0, color: "#FFFFFF" },
  { glyph: "sparkle",   x: 8,  y: 20, r:  12, d: 0.38, scale: 0.9, color: "#FCD34D" },
  { glyph: "eye",       x: 94, y: 72, r: -10, d: 0.40, scale: 0.95, color: "#FFFFFF" },
  { glyph: "gavel",     x: 48, y: 76, r:  -4, d: 0.42, scale: 1.0, color: "#FCD34D" },
  { glyph: "sparkle",   x: 78, y: 22, r:  18, d: 0.44, scale: 0.8, color: "#FFFFFF" },
  { glyph: "heart",     x: 22, y: 54, r: -14, d: 0.46, scale: 0.8, color: "#FCD34D" },
];

function entryFor(x, y, style) {
  switch (style) {
    case "B": {
      // Confetti drop — items fall from above the viewport with slight x jitter
      const jitter = ((x - 50) / 50) * 30;
      return { x: jitter, y: -110 };
    }
    case "C": {
      // Iris bloom — items start AT the centerpiece, fly to their position
      return { x: -(x - 50) * 1.0, y: -(y - 50) * 1.0 };
    }
    case "D": {
      // Cascade wave — items start slightly behind their position, no scatter
      return { x: 0, y: 12 };
    }
    case "A":
    default: {
      // Spring scatter — items fly in from off-screen toward their final spot
      const cx = x - 50;
      const cy = y - 50;
      const len = Math.max(40, Math.hypot(cx, cy));
      return { x: (cx / len) * 95, y: (cy / len) * 95 };
    }
  }
}

function tileInitial(style, dx, dy, rotate, depth, tumble) {
  switch (style) {
    case "B":
      // Confetti drop with a 3D tumble — items fall from above, spinning
      // through Y/X in true perspective so they read as physical cards.
      return {
        opacity: 0,
        x: dx, y: dy, z: -380,
        rotate: rotate + 20,
        rotateY: tumble.ry,
        rotateX: -tumble.rx,
        scale: 0.85,
      };
    case "C":
      return {
        opacity: 0,
        x: dx, y: dy, z: -260,
        rotate: rotate + 60,
        rotateY: tumble.ry * 0.6,
        rotateX: tumble.rx * 0.6,
        scale: 0.2,
      };
    case "D":
      return {
        opacity: 0,
        x: dx, y: dy, z: -180,
        rotate,
        rotateY: tumble.ry * 0.4,
        rotateX: 0,
        scale: 0.85,
      };
    case "A":
    default:
      return {
        opacity: 0,
        x: dx, y: dy, z: -320,
        rotate: rotate + 30,
        rotateY: tumble.ry,
        rotateX: tumble.rx,
        scale: 0.45,
      };
  }
}

function tileTransition(style, delay) {
  switch (style) {
    case "B":
      // Gravity bounce — quick fall, settle
      return {
        delay,
        y: { type: "spring", stiffness: 220, damping: 16, mass: 1.0 },
        x: { type: "spring", stiffness: 160, damping: 18 },
        z: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
        rotate: { duration: 0.45 },
        rotateY: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
        rotateX: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
        opacity: { duration: 0.18 },
        scale: { duration: 0.4 },
        default: { duration: 0.45 },
      };
    case "C":
      // Iris bloom — quick scale-out from center
      return {
        delay,
        type: "spring", stiffness: 220, damping: 20, mass: 0.7,
      };
    case "D":
      // Cascade wave — gentle fade up
      return {
        delay,
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1],
      };
    case "A":
    default:
      return {
        delay,
        type: "spring", stiffness: 200, damping: 20, mass: 0.85,
        rotateY: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay },
        rotateX: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay },
        z: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay },
      };
  }
}

function Sticker({ glyph, color }) {
  const props = {
    width: 38, height: 38, viewBox: "0 0 24 24",
    fill: "none", stroke: color, strokeWidth: 2.2,
    strokeLinecap: "round", strokeLinejoin: "round",
  };
  switch (glyph) {
    case "lightning":
      return <svg {...props}><path d="M13 2 L4 14 L11 14 L10 22 L20 9 L13 9 Z" fill={color} fillOpacity="0.18" /></svg>;
    case "heart":
      return <svg {...props}><path d="M12 21s-7-4.5-9.3-9A5.3 5.3 0 0 1 12 6a5.3 5.3 0 0 1 9.3 6c-2.3 4.5-9.3 9-9.3 9z" /></svg>;
    case "sparkle":
      return <svg {...props}><path d="M12 3 L13.6 10.4 L21 12 L13.6 13.6 L12 21 L10.4 13.6 L3 12 L10.4 10.4 Z" fill={color} fillOpacity="0.22" /></svg>;
    case "eye":
      return <svg {...props}><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></svg>;
    case "gavel":
      return <svg {...props}><path d="M14 4 L20 10" /><path d="M11 7 L17 13" /><path d="M4 19 L13 10" /><path d="M3 21 L9 21" /></svg>;
    default:
      return null;
  }
}

function Tile({ src, alt, w, h, isCard, animate, style, lite, dx, dy, rotate, delay }) {
  // Per-tile organic-drift parameters — different ranges per tile keep the
  // motion from looking like a synchronized loop.  Indexed by `delay` so each
  // tile gets its own rhythm.
  const seed = delay * 17.31;
  const driftY = lite ? 4 : 8 + ((seed * 1.3) % 6);
  const driftX = lite ? 0 : 3 + ((seed * 2.1) % 5);
  const wobble = lite ? 0 : 2 + ((seed * 0.8) % 2);
  const dur    = lite ? 4.5 : 4.5 + ((seed * 1.7) % 2.5);
  const xDur   = 5.5 + ((seed * 0.9) % 3);
  const rDur   = 6   + ((seed * 1.1) % 3);

  // ── 3D perspective state ──
  // Each tile sits at a slightly different Z depth (-50…+50px) so the
  // constellation has real depth-of-field, not a flat parallax illusion.
  // The initial entry tumbles through 3D space (rotateY ±90°, rotateX ±35°)
  // then settles to a tiny final tilt (±6°) — like Polaroids fluttering
  // into place.  On lite (mobile) we keep a lighter 3D entry (±40° tumble,
  // no continuous Z/rotateY parallax) — gives the depth feeling without
  // dropping frames on slower phones.
  const depthFinal = lite ? 0 : -45 + ((seed * 3.7) % 90);
  const tumble = {
    ry: lite ? (((seed * 5.3) % 80) - 40) : (((seed * 5.3) % 160) - 80),
    rx: lite ? (((seed * 7.1) % 30) - 15) : (((seed * 7.1) % 60) - 30),
  };
  const ryFinal = lite ? 0 : (((seed * 0.7) % 12) - 6);   // ±6°
  const rxFinal = lite ? 0 : (((seed * 1.3) % 8) - 4);    // ±4°

  const img = (
    <img
      src={src}
      alt={alt}
      draggable={false}
      style={{
        width: w,
        height: h,
        display: "block",
        objectFit: "contain",
        borderRadius: isCard ? "6%" : undefined,
        boxShadow: isCard
          ? (lite
            ? "0 12px 18px rgba(0, 20, 35, 0.55), 0 0 0 2px rgba(255, 255, 255, 0.85)"
            : "0 26px 36px rgba(0, 20, 35, 0.65), 0 8px 14px rgba(0, 0, 0, 0.45), 0 0 0 2px rgba(255, 255, 255, 0.92)")
          : undefined,
        // On low-power devices (lite) use a single drop-shadow, not three —
        // stacked filters are GPU-expensive and cause animation chop.
        filter: isCard
          ? "none"
          : (lite
            ? "drop-shadow(0 12px 14px rgba(0, 0, 0, 0.45))"
            : "drop-shadow(0 28px 30px rgba(0, 20, 35, 0.55)) " +
              "drop-shadow(0 8px 14px rgba(0, 0, 0, 0.45)) " +
              "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.30))"),
      }}
    />
  );
  if (!animate) {
    return <div style={{ transform: `rotate(${rotate}deg)` }}>{img}</div>;
  }
  // On lite (mobile) mode: only animate Y (single axis), skip rotation
  // wobble + scale-breath + X drift to keep the framerate smooth.
  const floatAnimate = lite
    ? { y: [0, -driftY, 0] }
    : {
        y: [0, -driftY, 0, -(driftY * 0.6), 0],
        x: [0, driftX, -(driftX * 0.4), 0],
        rotate: [rotate, rotate + wobble, rotate - (wobble * 0.6), rotate],
        rotateY: [ryFinal, ryFinal + 4, ryFinal - 3, ryFinal],
        scale: [1, 1.04, 1, 1.02, 1],
      };
  const floatTransition = lite
    ? { y: { duration: dur, repeat: Infinity, ease: "easeInOut", delay: 0.6 + delay } }
    : {
        y:       { duration: dur,  repeat: Infinity, ease: "easeInOut", delay: 0.6 + delay },
        x:       { duration: xDur, repeat: Infinity, ease: "easeInOut", delay: 0.8 + delay },
        rotate:  { duration: rDur, repeat: Infinity, ease: "easeInOut", delay: 0.7 + delay },
        rotateY: { duration: rDur * 0.9, repeat: Infinity, ease: "easeInOut", delay: 0.7 + delay },
        scale:   { duration: dur * 0.85, repeat: Infinity, ease: "easeInOut", delay: 0.5 + delay },
      };
  return (
    <motion.div
      initial={tileInitial(style, dx, dy, rotate, depthFinal, tumble)}
      animate={{
        opacity: 1,
        x: 0, y: 0, z: depthFinal,
        rotate, rotateY: ryFinal, rotateX: rxFinal, scale: 1,
        transition: tileTransition(style, delay),
      }}
      style={{ willChange: "transform, opacity", transformStyle: "preserve-3d" }}
    >
      <motion.div animate={floatAnimate} transition={floatTransition} style={{ transformStyle: "preserve-3d" }}>
        {img}
      </motion.div>
    </motion.div>
  );
}

/* ---------------- Sparkle particle around the logo ---------------- */
const SPARKS = Array.from({ length: 26 }).map((_, i) => {
  const a = (i / 26) * Math.PI * 2;
  const r = 80 + (i % 4) * 22;
  return {
    x: Math.cos(a) * r,
    y: Math.sin(a) * r,
    delay: 0.9 + (i % 9) * 0.10,
    size: 5 + (i % 3) * 2,
  };
});

function SparkleField({ animate, scale, lite }) {
  if (!animate) return null;
  // Use 14 sparkles on lite (mobile), 26 on desktop.
  const sparks = lite ? SPARKS.slice(0, 14) : SPARKS;
  return (
    <div className="bidora-splash-sparks">
      {sparks.map((s, i) => (
        <motion.span
          key={i}
          className="bidora-splash-spark"
          style={{
            left: `calc(50% + ${s.x * scale}px)`,
            top: `calc(50% + ${s.y * scale}px)`,
            width: s.size * scale,
            height: s.size * scale,
          }}
          initial={{ opacity: 0, scale: 0 }}
          animate={{
            opacity: [0, 1, 0],
            scale: [0, 1.4, 0],
            transition: {
              delay: s.delay,
              duration: 1.6,
              repeat: Infinity,
              repeatDelay: 0.4 + (i % 5) * 0.18,
              ease: "easeInOut",
            },
          }}
        />
      ))}
    </div>
  );
}

/* ---------------- Bidorah hero wordmark ----------------
 * A massive "Bidorah" wordmark that scales from huge + blurred down to
 * its final size with an elastic 3D spring bounce — tilting forward from
 * a deep -Z position with the top edge initially leaning back, then
 * snapping flat to the camera.  After settling it floats with a subtle
 * continuous parallax (rotateY ±4°, rotateX ±2°, z micro-drift) so the
 * brand feels alive instead of static.
 *
 * On variant A the wordmark holds center stage briefly before the product
 * constellation arrives around it.  On B everything plays together.  On
 * C only this wordmark plays — no constellation at all.
 */
function BidorahWordmark({ scale, animate, lite }) {
  const size = Math.round((lite ? 88 : 132) * scale);
  return (
    <motion.div
      className="bidora-hero-wordmark-wrap"
      style={{ transformStyle: "preserve-3d", willChange: "transform, opacity, filter" }}
      initial={animate ? {
        opacity: 0,
        scale: lite ? 2.8 : 3.2,
        filter: "blur(28px)",
        z: -500,
        rotateX: lite ? 25 : 55,
        rotateY: lite ? -8 : -18,
      } : { opacity: 1, scale: 1 }}
      animate={animate ? {
        opacity: 1,
        scale: 1,
        filter: "blur(0px)",
        z: 0,
        rotateX: 0,
        rotateY: 0,
        transition: {
          opacity: { duration: 0.38, ease: [0.22, 1, 0.36, 1] },
          filter: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
          scale: { type: "spring", stiffness: 130, damping: 12, mass: 1.0 },
          z: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
          rotateX: { type: "spring", stiffness: 110, damping: 14, mass: 0.9 },
          rotateY: { type: "spring", stiffness: 110, damping: 14, mass: 0.9 },
        },
      } : {}}
    >
      <motion.span
        className="bidora-hero-wordmark"
        style={{ fontSize: size, transformStyle: "preserve-3d", display: "inline-flex", alignItems: "baseline" }}
        animate={animate && !lite ? {
          scale: [1, 1.03, 1],
          rotateY: [0, 4, 0, -4, 0],
          rotateX: [0, -2, 0, 2, 0],
          z: [0, 18, 0, 12, 0],
        } : animate ? { scale: [1, 1.025, 1] } : {}}
        transition={animate && !lite ? {
          scale: { duration: 3.0, repeat: Infinity, ease: "easeInOut", delay: 0.9 },
          rotateY: { duration: 4.2, repeat: Infinity, ease: "easeInOut", delay: 0.9 },
          rotateX: { duration: 4.2, repeat: Infinity, ease: "easeInOut", delay: 1.1 },
          z: { duration: 3.6, repeat: Infinity, ease: "easeInOut", delay: 1.0 },
        } : animate ? { duration: 2.4, repeat: Infinity, ease: "easeInOut", delay: 0.9 } : {}}
      >
        Bidorah
        <span aria-hidden="true" className="bidora-hero-wordmark-dot" style={{
          width: Math.max(6, Math.round(size * 0.09)),
          height: Math.max(6, Math.round(size * 0.09)),
          marginLeft: Math.round(size * 0.04),
        }} />
      </motion.span>
    </motion.div>
  );
}

export default function SplashIntro() {
  const animStyle = React.useMemo(readAnimStyle, []);
  const introStyle = React.useMemo(readIntroStyle, []);
  const [scale, setScale] = React.useState(readSplashScale);

  React.useEffect(() => {
    const onResize = () => setScale(readSplashScale());
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  // When the user is previewing a style via ?splash=X or an explicit ?intro=X
  // we ignore the once-per-session cache so they can reload to compare easily.
  // Variant B is now the default so its presence in state doesn't count as
  // preview mode — only an *explicit* URL param does.
  const previewMode = animStyle !== "A"
    || (typeof window !== "undefined" && /[?&](splash|intro)=/.test(window.location.search));

  const [visible, setVisible] = React.useState(() => {
    if (typeof window === "undefined") return false;
    if (previewMode) return true;
    try { return !sessionStorage.getItem(STORAGE_KEY); } catch { return true; }
  });
  const reducedMotion = useReducedMotion();

  React.useEffect(() => {
    if (!visible) return;
    if (!previewMode) {
      try { sessionStorage.setItem(STORAGE_KEY, "1"); } catch { /* private mode */ }
    }
    const t = setTimeout(() => setVisible(false), DURATION_MS);
    return () => clearTimeout(t);
  }, [visible, previewMode]);

  React.useEffect(() => {
    if (!visible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [visible]);

  const dismiss = React.useCallback(() => setVisible(false), []);
  const animate = !reducedMotion;
  // "Lite" rendering on phones / tablets: simpler animations, single-axis
  // float, single drop-shadow, fewer sparkles, no shimmer/halo CSS animation.
  const lite = scale < 0.85;

  // ─── Bidorah hero intro gating ───
  // When `?intro=` is set, the Bidorah hero wordmark bounce plays.
  //   A → wordmark bounces in alone for ~1.5s, then the constellation reveals.
  //   B → wordmark + constellation appear simultaneously.
  //   C → ONLY the wordmark plays (no constellation at all).
  const hideConstellation = introStyle === "C";
  const [showConstellation, setShowConstellation] = React.useState(() => introStyle !== "A" && introStyle !== "C");
  React.useEffect(() => {
    if (introStyle !== "A") return;
    const t = setTimeout(() => setShowConstellation(true), 1500);
    return () => clearTimeout(t);
  }, [introStyle]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className={`bidora-splash${lite ? " bidora-splash--lite" : ""}`}
          role="dialog"
          aria-label="Welcome to Bidora"
          data-testid="splash-intro"
          onClick={dismiss}
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.04, filter: "blur(8px)", transition: { duration: 0.35, ease: [0.32, 0, 0.67, 0] } }}
        >
          <div className="bidora-splash-spotlight" />
          <div className="bidora-splash-grain" />

          {/* Floating product PNGs — gated by intro variant */}
          {!hideConstellation && showConstellation && TILES.map((t, i) => {
            const entry = entryFor(t.x, t.y, animStyle);
            return (
              <div
                key={`tile-${i}`}
                className="bidora-splash-tile-pos"
                style={{ left: `${t.x}%`, top: `${t.y}%` }}
              >
                <Tile
                  src={t.src}
                  alt={t.alt}
                  w={Math.round(t.w * scale)}
                  h={Math.round(t.h * scale)}
                  isCard={t.isCard}
                  animate={animate}
                  style={animStyle}
                  lite={lite}
                  dx={entry.x}
                  dy={entry.y}
                  rotate={t.r}
                  delay={t.d * 0.6}
                />
              </div>
            );
          })}

          {/* Doodle stickers — gated alongside the constellation */}
          {!hideConstellation && showConstellation && STICKERS.map((s, i) => (
            <motion.div
              key={`sticker-${i}`}
              className="bidora-splash-sticker"
              style={{ left: `${s.x}%`, top: `${s.y}%` }}
              initial={animate ? { opacity: 0, scale: 0.4, rotate: s.r - 20 } : { opacity: 1, scale: s.scale, rotate: s.r }}
              animate={animate ? {
                opacity: 1, scale: s.scale, rotate: s.r,
                transition: { delay: s.d, type: "spring", stiffness: 200, damping: 14 },
              } : {}}
            >
              <motion.div
                animate={animate ? { rotate: [s.r, s.r + 4, s.r - 4, s.r] } : {}}
                transition={animate ? { duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.6 + s.d } : {}}
              >
                <Sticker glyph={s.glyph} color={s.color} />
              </motion.div>
            </motion.div>
          ))}

          {/* Sparkle field around the logo */}
          <SparkleField animate={animate} scale={scale} lite={lite} />

          {/* Centerpiece — either the Bidorah hero wordmark bounce
              (when ?intro=A|B|C is set) or the original spinning
              diamond logo. */}
          {introStyle ? (
            <div className="bidora-splash-center">
              <BidorahWordmark scale={scale} animate={animate} lite={lite} />
            </div>
          ) : (
            <motion.div
              className="bidora-splash-center"
              initial={animate ? { opacity: 0, scale: 0.5, filter: "blur(14px)" } : { opacity: 1, scale: 1 }}
              animate={animate ? {
                opacity: 1, scale: 1, filter: "blur(0px)",
                transition: { delay: 0.45, duration: 0.55, ease: [0.22, 1, 0.36, 1] },
              } : {}}
            >
              <div className="bidora-splash-mark-wrap" style={{ "--splash-scale": scale }}>
                {/* Halo behind the spinning mark */}
                <div className="bidora-splash-halo" />
                {/* Diamond inspection wobble — tilts ±28° on Y axis, then back.
                    Never passes through 90° so the flat 2D mark never collapses
                    to an edge-on hairline. */}
                <motion.div
                  className="bidora-splash-mark-spin"
                  animate={animate ? { rotateY: [0, 28, 0, -28, 0] } : {}}
                  transition={animate ? { duration: 4.5, repeat: Infinity, ease: "easeInOut" } : {}}
                >
                  <BidoraMark size={Math.round(120 * scale)} animate={animate} />
                </motion.div>
              </div>
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
